/**
 * GMA-1622 (CompassCare Web 7/14 — Dispatch) — call trees & escalation page.
 *
 * Mirrors the hi-fi mockup (CompassCare Dispatch.dc.html): a CALL TREE LIBRARY
 * rail (org default + house/client overrides, managed by Caregiver Admins) and
 * a selected-tree panel — escalation levels L1→L4 with contacts, methods and
 * wait windows, the "Alert fires → escalation begins immediately" banner, the
 * 911 note, per-tree escalation rules, Contact Attempts Today audit rows, and
 * a **Test Call Tree** modal that runs the REAL dispatchModel.simulateCallTree
 * with the tree's deterministic demo responses — clearly labeled
 * "SIMULATION — NO REAL CONTACTS", it never contacts anyone.
 *
 * Management actions (New Call Tree / Add Contact / Save test to audit) are
 * honest stubs: they queue a note naming the follow-up instead of pretending.
 * Fail-safe: no trees renders a calm empty state; malformed levels are
 * tolerated by the model. Never a throw.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { theme } from "../../styles/designTokens";
import { simulateCallTree } from "./dispatchModel";

const NORA_PURPLE = "#8B7CF6";
const OK_GREEN = "#2ECC8F";
const WARN_ORANGE = "#F5883D";
const CRIT_RED = "#F0524E";

const METHOD_LABEL = { call: "Call", text: "Text", email: "Email", app: "App" };
const OUTCOME_TINT = { answered: OK_GREEN, no_answer: WARN_ORANGE, declined: CRIT_RED };
const OUTCOME_LABEL = { answered: "Answered", no_answer: "No answer", declined: "Declined" };

/** 180000 -> "3 min", 90000 -> "1.5 min", 0 -> "—". */
export function formatWaitMs(ms) {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return "—";
  const min = ms / 60000;
  const rounded = Math.round(min * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded} min`;
}

/** 180000 -> "+3:00" timeline offset. */
export function formatAtMs(ms) {
  const total = typeof ms === "number" && Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `+${m}:${s}`;
}

function SectionLabel({ children }) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: theme.textMuted, textTransform: "uppercase" }}>
      {children}
    </span>
  );
}

SectionLabel.propTypes = { children: PropTypes.node };

function StubNote({ id, children }) {
  return (
    <span data-testid={id} style={{ color: theme.textMuted, fontSize: 11.5 }}>
      {children}
    </span>
  );
}

StubNote.propTypes = { id: PropTypes.string.isRequired, children: PropTypes.node };

function TreeCard({ tree, selected, onSelect }) {
  return (
    <button
      type="button"
      data-testid="tree-card"
      data-tree-id={tree.id}
      onClick={() => onSelect(tree)}
      aria-current={selected ? "true" : undefined}
      style={{
        display: "grid",
        gap: 4,
        textAlign: "left",
        padding: "10px 12px",
        borderRadius: 10,
        cursor: "pointer",
        background: selected ? theme.surface : "transparent",
        border: `1px solid ${selected ? NORA_PURPLE : theme.border}`,
        color: theme.textPrimary,
        font: `13px ${theme.fontUI}`,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{tree.name}</strong>
        {tree.override ? (
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.8,
              color: WARN_ORANGE,
              border: `1px solid ${WARN_ORANGE}55`,
              background: `${WARN_ORANGE}14`,
              borderRadius: 5,
              padding: "1px 6px",
            }}
          >
            OVERRIDE
          </span>
        ) : null}
      </span>
      <span style={{ color: theme.textMuted, fontSize: 11.5 }}>{tree.scope}</span>
    </button>
  );
}

TreeCard.propTypes = {
  tree: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

function LevelRow({ level }) {
  return (
    <div
      data-testid="tree-level"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        border: `1px solid ${theme.border}`,
        borderRadius: 9,
        padding: "9px 11px",
      }}
    >
      <span
        style={{
          flex: "none",
          minWidth: 30,
          textAlign: "center",
          fontFamily: theme.fontMono,
          fontWeight: 700,
          fontSize: 12,
          color: NORA_PURPLE,
          border: `1px solid ${NORA_PURPLE}44`,
          background: `${NORA_PURPLE}12`,
          borderRadius: 6,
          padding: "2px 5px",
        }}
      >
        {level.level}
      </span>
      <span style={{ display: "grid", gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{level.contactName || "Unassigned"}</span>
        {level.role ? <span style={{ color: theme.textMuted, fontSize: 11.5 }}>{level.role}</span> : null}
        {level.note ? <span style={{ color: theme.textMuted, fontSize: 11 }}>{level.note}</span> : null}
      </span>
      <span style={{ display: "grid", gap: 3, justifyItems: "end", flex: "none" }}>
        <span style={{ display: "flex", gap: 4 }}>
          {(level.methods || []).map((m) =>
            METHOD_LABEL[m] ? (
              <span
                key={m}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: theme.textMuted,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 5,
                  padding: "1px 6px",
                }}
              >
                {METHOD_LABEL[m]}
              </span>
            ) : null,
          )}
        </span>
        <span style={{ color: OK_GREEN, fontFamily: theme.fontMono, fontSize: 11 }}>
          {formatWaitMs(level.escalationMs)}
          {level.retries > 1 ? ` · ×${level.retries}` : ""}
        </span>
      </span>
    </div>
  );
}

LevelRow.propTypes = { level: PropTypes.object.isRequired };

function TestModal({ tree, onClose }) {
  const [savedNote, setSavedNote] = useState(false);
  const result = useMemo(
    () => simulateCallTree(tree, tree.simResponses || {}),
    [tree],
  );
  const summary = result.resolved
    ? `Resolved by ${result.resolvedBy} after ${formatAtMs(result.totalMs).slice(1)} — simulated, no one was contacted.`
    : "Not resolved in simulation — the tree would keep running until a human confirms contact.";
  return (
    <div
      role="dialog"
      aria-label={`Call tree test — ${tree.name}`}
      data-testid="tree-test-modal"
      style={{
        border: `1px solid ${NORA_PURPLE}66`,
        borderRadius: 12,
        padding: 14,
        background: theme.surface,
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13.5 }}>Call Tree Test · {tree.name}</strong>
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 0.8,
            color: NORA_PURPLE,
            border: `1px solid ${NORA_PURPLE}55`,
            background: `${NORA_PURPLE}14`,
            borderRadius: 5,
            padding: "2px 7px",
          }}
        >
          SIMULATION — NO REAL CONTACTS
        </span>
      </div>
      <div data-testid="tree-test-timeline" style={{ display: "grid", gap: 6 }}>
        {result.timeline.map((step) => (
          <div key={`${step.level}-${step.atMs}`} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5 }}>
            <span style={{ fontFamily: theme.fontMono, color: theme.textMuted, minWidth: 44 }}>
              {formatAtMs(step.atMs)}
            </span>
            <span style={{ fontFamily: theme.fontMono, fontWeight: 700, color: NORA_PURPLE, minWidth: 26 }}>
              {step.level}
            </span>
            <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {step.contactName || "Unassigned"}
              {step.method ? (
                <span style={{ color: theme.textMuted }}> · {METHOD_LABEL[step.method] || step.method}</span>
              ) : null}
              {step.retries > 1 ? <span style={{ color: theme.textMuted }}> · ×{step.retries}</span> : null}
            </span>
            <span style={{ color: OUTCOME_TINT[step.outcome] || theme.textMuted, fontWeight: 700, fontSize: 11.5 }}>
              {OUTCOME_LABEL[step.outcome] || step.outcome}
            </span>
          </div>
        ))}
      </div>
      <span data-testid="tree-test-summary" style={{ fontSize: 12.5, color: result.resolved ? OK_GREEN : WARN_ORANGE }}>
        {summary}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="button"
          data-testid="tree-test-save-btn"
          onClick={() => setSavedNote(true)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${theme.border}`,
            cursor: "pointer",
            background: "transparent",
            color: theme.textPrimary,
            font: `700 12px ${theme.fontUI}`,
          }}
        >
          Save test result to audit log
        </button>
        <button
          type="button"
          data-testid="tree-test-close-btn"
          onClick={onClose}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            background: NORA_PURPLE,
            color: "#0A0D13",
            font: `700 12px ${theme.fontUI}`,
          }}
        >
          Close
        </button>
      </div>
      {savedNote ? (
        <StubNote id="tree-test-save-note">
          Test results write to the audit log once live dispatch wiring lands (GMA-1622 follow-up).
        </StubNote>
      ) : null}
    </div>
  );
}

TestModal.propTypes = { tree: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired };

export default function DispatchPage({ trees = [], attempts = [], now = Date.now() }) {
  const list = useMemo(() => (Array.isArray(trees) ? trees.filter(Boolean) : []), [trees]);
  const [selectedId, setSelectedId] = useState(null);
  const [testing, setTesting] = useState(false);
  const [newTreeNote, setNewTreeNote] = useState(false);
  const [addContactNote, setAddContactNote] = useState(false);
  const selected = list.find((t) => t.id === selectedId) || list[0] || null;

  const minsAgo = (at) => {
    if (typeof at !== "number" || !Number.isFinite(at)) return "—";
    const m = Math.max(0, Math.round((now - at) / 60000));
    return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ${m % 60}m ago`;
  };

  return (
    <div
      data-testid="dispatch-page"
      style={{
        fontFamily: theme.fontUI,
        color: theme.textPrimary,
        display: "grid",
        gridTemplateColumns: "minmax(250px, 320px) minmax(0, 1fr)",
        gap: 12,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 10 }}>
        <SectionLabel>Call Tree Library</SectionLabel>
        <span style={{ color: theme.textMuted, fontSize: 11.5 }}>
          Groups, call trees & dispatch are managed by Caregiver Admins.
        </span>
        <div data-testid="tree-library" style={{ display: "grid", gap: 8 }}>
          {list.length === 0 ? (
            <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
              No call trees configured. Caregiver Admins define escalation trees
              in dispatch settings.
            </span>
          ) : (
            list.map((t) => (
              <TreeCard
                key={t.id}
                tree={t}
                selected={selected ? selected.id === t.id : false}
                onSelect={(tree) => {
                  setSelectedId(tree.id);
                  setTesting(false);
                }}
              />
            ))
          )}
        </div>
        <button
          type="button"
          data-testid="new-tree-btn"
          onClick={() => setNewTreeNote(true)}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px dashed ${theme.border}`,
            cursor: "pointer",
            background: "transparent",
            color: theme.textMuted,
            font: `700 12px ${theme.fontUI}`,
          }}
        >
          + New Call Tree
        </button>
        {newTreeNote ? (
          <StubNote id="new-tree-note">
            Tree management wires to the Caregiver Admin backend (GMA-1622 follow-up).
          </StubNote>
        ) : null}
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {selected ? (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "grid", gap: 3, minWidth: 0 }}>
                <strong style={{ fontSize: 16 }}>{selected.name}</strong>
                <span style={{ color: theme.textMuted, fontSize: 12.5 }}>{selected.desc}</span>
                {selected.overrideNote ? (
                  <span style={{ color: WARN_ORANGE, fontSize: 11.5 }}>{selected.overrideNote}</span>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 8, flex: "none" }}>
                <button
                  type="button"
                  data-testid="add-contact-btn"
                  onClick={() => setAddContactNote(true)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${theme.border}`,
                    cursor: "pointer",
                    background: "transparent",
                    color: theme.textPrimary,
                    font: `700 12px ${theme.fontUI}`,
                  }}
                >
                  Add Contact
                </button>
                <button
                  type="button"
                  data-testid="test-tree-btn"
                  onClick={() => setTesting(true)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background: NORA_PURPLE,
                    color: "#0A0D13",
                    font: `700 12px ${theme.fontUI}`,
                  }}
                >
                  Test Call Tree
                </button>
              </div>
            </div>
            {addContactNote ? (
              <StubNote id="add-contact-note">
                Contact management wires to the Caregiver Admin backend (GMA-1622 follow-up).
              </StubNote>
            ) : null}

            {testing ? <TestModal tree={selected} onClose={() => setTesting(false)} /> : null}

            <div
              style={{
                border: `1px solid ${OK_GREEN}44`,
                background: `${OK_GREEN}0d`,
                borderRadius: 9,
                padding: "9px 12px",
                fontSize: 12.5,
              }}
            >
              <strong>Alert fires</strong>
              <span style={{ color: theme.textMuted }}>
                {" "}→ escalation begins immediately · every attempt is audit-logged
              </span>
            </div>

            <div style={{ display: "grid", gap: 7 }}>
              {(selected.levels || []).map((lvl) => (
                <LevelRow key={lvl.level} level={lvl} />
              ))}
            </div>

            <div
              style={{
                border: `1px solid ${CRIT_RED}44`,
                background: `${CRIT_RED}0d`,
                borderRadius: 9,
                padding: "9px 12px",
                fontSize: 12,
                color: theme.textMuted,
              }}
            >
              <strong style={{ color: CRIT_RED }}>Emergency services</strong> — 911 is called
              directly by any responder at any time if life-threatening; the tree keeps
              running until a human confirms client contact.
            </div>

            {selected.rules && selected.rules.length ? (
              <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 6 }}>
                <SectionLabel>Escalation Rules — {selected.name}</SectionLabel>
                {selected.rules.map((r) => (
                  <span key={r} style={{ fontSize: 12.5, color: theme.textMuted }}>
                    • {r}
                  </span>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
            Select a call tree to see its escalation levels.
          </span>
        )}

        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 7 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <SectionLabel>Contact Attempts Today</SectionLabel>
            <span style={{ color: theme.textMuted, fontSize: 11 }}>full audit →</span>
          </div>
          <div data-testid="contact-attempts" style={{ display: "grid", gap: 6 }}>
            {(attempts || []).length === 0 ? (
              <span style={{ color: theme.textMuted, fontSize: 12.5 }}>No contact attempts today.</span>
            ) : (
              attempts.map((a) => (
                <div key={a.id} style={{ display: "flex", gap: 9, alignItems: "baseline", fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700, flex: "none" }}>{a.action}</span>
                  <span style={{ color: theme.textMuted, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.target} — {a.detail}
                  </span>
                  <span style={{ color: theme.textMuted, fontFamily: theme.fontMono, fontSize: 11, flex: "none" }}>
                    {minsAgo(a.at)} · {a.who}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

DispatchPage.propTypes = {
  trees: PropTypes.arrayOf(PropTypes.object),
  attempts: PropTypes.arrayOf(PropTypes.object),
  now: PropTypes.number,
};
